import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../shared/controls/pageable-table/pageable-table.component';
import { AutoCompleteDropdownComponent } from '../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { DialogService } from '../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { InitializerService } from '../../../shared/services/initializer-service';
import { AuthService } from '../../../shared/services/auth.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { LoadingHelper } from '../../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../../shared/constants/area.constants';
import { PermissionConstants } from '../../../shared/constants/permission.constants';

import { SalesService, PeriodService, SalesBatchItem, PagingData, AllPeriodItem } from '../../../../service/proxy';

@Component({
    templateUrl: './sales-list.component.html',
    styleUrls: ['./sales-list.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        AutoCompleteDropdownComponent,
        HasPermissionDirective,
    ],

    providers: [
        SalesService,
        PeriodService
    ]
})
export class SalesListComponent implements OnInit {
    public initialized = false;

    public selectedStatus: boolean = true;
    public selectedPeriodId: number = 0;
    public search: string = '';

    public paging: PagingData = new PagingData();
    public periods: AllPeriodItem[] = [];
    public batches: SalesBatchItem[] = [];

    public statusOptions = [
        { id: 0, label: 'Pending' },
        { id: 1, label: 'Completed' }
    ];

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    constructor(
        public authService: AuthService,
        private router: Router,
        private salesService: SalesService,
        private initializerService: InitializerService,
        private periodService: PeriodService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private dialogService: DialogService)
    {
        this.paging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        this.periodService.getAllPeriods()
            .subscribe((data) => {
                this.initialized = true;
                this.periods = data.results || [];
                this.selectedPeriodId = this.periods[1]?.id ?? 0;

                this.initializePermissions();
                this.refreshGridData();
            }
        );
    }

    initializePermissions(): void {
        this.showEdit =
            this.selectedStatus === false && this.authService.hasPermission(AreaConstants.Sales, PermissionConstants.Import);

        this.showDelete =
            (this.selectedStatus === false && this.authService.hasPermission(AreaConstants.Sales, PermissionConstants.DeletePending)) ||
            (this.selectedStatus === true && this.authService.hasPermission(AreaConstants.Sales, PermissionConstants.DeleteCompleted));
    }

    formatPeriod = (period: AllPeriodItem): string =>
        `${period?.year}-${String(period?.month).padStart(2, '0')}`;

    displayStatus = (status: any): string => status?.label ?? '';

    onStatusSelectionChange(statusId: number | null): void {
        this.selectedStatus = statusId === 1;
        this.refreshGridData();
        this.initializePermissions();
    }

    onPeriodSelectionChange(periodId: number | null): void {
        this.selectedPeriodId = periodId ?? 0;
        this.refreshGridData();
    }

    getColumnMap(): Record<string, string> {
        const base: Record<string, string> = {
            createdAt: 'Date Created',
            fileName: 'Filename',
            affectedPolicies: 'Client Policies',
            clientName: 'Client',
            period: 'Period',
        };

        return this.selectedStatus
            ? { ...base, completedAt: 'Date Completed' }
            : { ...base, updatedAt: 'Date Updated' };
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getSalesBatches(this.paging);
    }

    getSalesBatches(pagingData: PagingData): void {
        if (!this.initialized) return;

        const search = `${this.selectedPeriodId}|${this.search}`;

        this.salesService.getSalesBatches(this.selectedStatus, search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.batches = data.results || [];
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.batches = [];
                        this.paging = this.initializerService.createPagingData();
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    createSalesBatch(): void {
        this.router.navigate([`/sales-create`]);
    }

    editSalesBatch(batchId: number): void {
        sessionStorage.setItem('isRedirected', 'true');
        this.router.navigate([`/sales-wizard/${batchId}`]);
    }

    deleteSalesBatch(batchId: number): void {
        const title = this.selectedStatus
            ? 'Delete COMPLETED Batch'
            : 'Delete Pending Batch';

        const message = this.selectedStatus
            ? 'You are about to delete a COMPLETED batch. Are you sure?'
            : 'Are you sure you want to delete this batch?';

        const toastMessage = this.selectedStatus
            ? 'Completed batch deleted successfully.'
            : 'Pending batch deleted successfully.';

        if (!this.selectedStatus) {
            this.deletePendingSalesBatch(batchId, title, message, toastMessage);
        }
        else {
            this.deleteCompletedSalesBatch(batchId, title, message, toastMessage);
        };
    }

    deletePendingSalesBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.salesService.deletePendingSalesBatch(batchId)
                        .subscribe({
                            next: () => {
                                this.getSalesBatches(this.paging);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            },
                            complete: () => {
                                this.toastService.show(toastMessage, 'success');
                            }
                        });
                }
            }
        );
    }

    deleteCompletedSalesBatch(batchId: number, title: string, message: string, toastMessage: string): void {
        this.dialogService.show(title, message, 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.salesService.deleteCompletedSalesBatch(batchId)
                        .subscribe({
                            next: () => {
                                this.getSalesBatches(this.paging);
                            },
                            error: (error: any) => {
                                this.errorService.handleError(error);
                            },
                            complete: () => {
                                this.toastService.show(toastMessage, 'success');
                            }
                        });
                }
            }
        );
    }
}
